import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, Check, X, Star, Shield, Settings, Tag, List, Eye, ExternalLink } from "lucide-react";
import { http, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const TABS = [
  { id: "pending", label: "Pending Review", icon: List },
  { id: "listings", label: "All Listings", icon: Eye },
  { id: "categories", label: "Categories", icon: Tag },
  { id: "settings", label: "Settings", icon: Settings },
];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-gray-900 text-lg">{title}</h3><button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
}

// ── Pending Listings ──────────────────────────────────────────────────────────
function PendingTab() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState(null);
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    http.get("/admin/marketplace/listings", { params: { status: "pending" } })
      .then(r => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setActing(id);
    try { await http.put(`/admin/marketplace/listings/${id}/approve`); toast.success("Approved!"); load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setActing(null); }
  };

  const reject = async () => {
    if (!rejectModal) return;
    setActing(rejectModal.id);
    try { await http.put(`/admin/marketplace/listings/${rejectModal.id}/reject`, { reason }); toast.success("Rejected"); setRejectModal(null); setReason(""); load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setActing(null); }
  };

  if (loading) return <div className="animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 rounded-2xl bg-gray-100" />)}</div>;

  return (
    <div className="space-y-3">
      {listings.length === 0 && <div className="text-center py-12 text-gray-400">No pending listings. 🎉</div>}
      {listings.map(l => (
        <div key={l.id} className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 shrink-0">
              {l.logo_url ? <img src={l.logo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center font-bold text-lg" style={{ background: "#0F1E38", color: "#C5A028" }}>{(l.name || "?")[0]}</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-900">{l.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">{l.category_name} · {l.location}</div>
              <div className="text-xs text-gray-400 mt-0.5">by {l.owner_name} · {new Date(l.created_at).toLocaleDateString("en-GB")}</div>
            </div>
            <Link to={`/marketplace/business/${l.id}`} target="_blank" className="p-2 text-gray-400 hover:text-blue-600"><ExternalLink size={14} /></Link>
          </div>
          {l.description && <p className="text-sm text-gray-600 line-clamp-2">{l.description}</p>}
          {l.phone && <div className="text-xs text-gray-500">📞 {l.phone} · {l.email}</div>}
          <div className="flex gap-2 pt-1">
            <button onClick={() => approve(l.id)} disabled={acting === l.id} className="flex items-center gap-1.5 px-4 py-2 rounded-xl font-bold text-white text-sm bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50">
              <Check size={14} /> Approve
            </button>
            <button onClick={() => { setRejectModal(l); setReason(""); }} className="flex items-center gap-1.5 px-4 py-2 rounded-xl font-bold text-white text-sm bg-red-500 hover:bg-red-600 transition-colors">
              <X size={14} /> Reject
            </button>
          </div>
        </div>
      ))}
      {rejectModal && (
        <Modal title="Reject Listing" onClose={() => setRejectModal(null)}>
          <p className="text-sm text-gray-600 mb-3">Rejecting: <strong>{rejectModal.name}</strong></p>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none resize-none mb-4" placeholder="Reason for rejection (will be shown to the owner)…" />
          <div className="flex gap-3">
            <button onClick={() => setRejectModal(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold">Cancel</button>
            <button onClick={reject} disabled={!reason.trim() || !!acting} className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm bg-red-500 disabled:opacity-50">Reject</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── All Listings ───────────────────────────────────────────────────────────────
function AllListingsTab() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    const params = filter !== "all" ? { status: filter } : {};
    http.get("/admin/marketplace/listings", { params }).then(r => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const action = async (id, endpoint) => {
    try {
      await http.put(`/admin/marketplace/listings/${id}/${endpoint}`);
      toast.success("Done");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const STATUS_COLORS = { pending: "bg-yellow-50 text-yellow-700", approved: "bg-green-50 text-green-700", rejected: "bg-red-50 text-red-700", suspended: "bg-gray-100 text-gray-600" };

  if (loading) return <div className="animate-pulse space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-14 rounded-xl bg-gray-100" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
        {["all", "approved", "pending", "rejected", "suspended"].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors capitalize ${filter === s ? "text-white border-transparent bg-gray-900" : "border-gray-200 text-gray-600"}`}>{s}</button>
        ))}
      </div>
      <div className="text-xs text-gray-400">{listings.length} listings</div>
      {listings.map(l => (
        <div key={l.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100">
          <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 shrink-0">
            {l.logo_url ? <img src={l.logo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center font-bold" style={{ background: "#0F1E38", color: "#C5A028" }}>{(l.name || "?")[0]}</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-gray-900 truncate">{l.name}</div>
            <div className="text-xs text-gray-400">{l.category_name} · {l.owner_name}</div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[l.status] || STATUS_COLORS.pending}`}>{l.status}</span>
            {l.verified && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 flex items-center gap-1"><Shield size={10} />V</span>}
            {l.featured && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">★</span>}
          </div>
          <div className="flex gap-1 shrink-0">
            {l.status !== "approved" && <button onClick={() => action(l.id, "approve")} title="Approve" className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"><Check size={13} /></button>}
            <button onClick={() => action(l.id, l.featured ? "unfeature" : "feature")} title="Toggle Featured" className="p-1.5 text-yellow-500 hover:bg-yellow-50 rounded-lg"><Star size={13} /></button>
            <button onClick={() => action(l.id, l.verified ? "unverify" : "verify")} title="Toggle Verified" className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"><Shield size={13} /></button>
            <Link to={`/marketplace/business/${l.id}`} target="_blank" className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg"><ExternalLink size={13} /></Link>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Categories ─────────────────────────────────────────────────────────────────
function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", icon: "", description: "" });
  const [editId, setEditId] = useState(null);

  const load = () => http.get("/admin/marketplace/categories").then(r => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", slug: "", icon: "", description: "" }); setEditId(null); setModal(true); };
  const openEdit = (c) => { setForm(c); setEditId(c.id); setModal(true); };

  const save = async () => {
    try {
      if (editId) await http.put(`/admin/marketplace/categories/${editId}`, form);
      else await http.post("/admin/marketplace/categories", form);
      toast.success("Saved"); setModal(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete category?")) return;
    try { await http.delete(`/admin/marketplace/categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}><Plus size={15} /> New Category</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {cats.map(c => (
          <div key={c.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100">
            <span className="text-2xl">{c.icon || "🔧"}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 text-sm">{c.name}</div>
              <div className="text-xs text-gray-400">/{c.slug}{c.listing_count > 0 ? ` · ${c.listing_count} listings` : ""}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(c)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg"><Edit2 size={13} /></button>
              <button onClick={() => del(c.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      {modal && (
        <Modal title={editId ? "Edit Category" : "New Category"} onClose={() => setModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Name *</label>
                <input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/\s+/g, "-") }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Slug</label>
                <input value={form.slug || ""} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none font-mono" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Icon (emoji)</label>
              <input value={form.icon || ""} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" placeholder="🔧" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Description</label>
              <textarea value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none resize-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold">Cancel</button>
              <button onClick={save} className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}>Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Settings ───────────────────────────────────────────────────────────────────
function SettingsTab() {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { http.get("/admin/marketplace/settings").then(r => setSettings(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const setHero = (k, v) => setSettings(s => ({ ...s, hero: { ...(s.hero || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await http.put("/admin/marketplace/settings", settings); toast.success("Settings saved!"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const Inp = ({ k, placeholder, type = "text" }) => (
    <input type={type} value={settings[k] || ""} onChange={e => set(k, e.target.value)} placeholder={placeholder} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400" />
  );

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="font-bold text-gray-900 mb-2">General</div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Marketplace Name</label><Inp k="marketplace_name" placeholder="CPM Marketplace" /></div>
          <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Tagline</label><Inp k="tagline" placeholder="Find trusted community services" /></div>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Admin Notification Email(s)</label>
          <Inp k="admin_email" placeholder="admin@celestialpeoplemeet.com (comma-separated)" />
        </div>
        <div className="space-y-2">
          {[
            { k: "reviews_enabled", l: "Enable reviews & ratings" },
            { k: "gallery_enabled", l: "Enable gallery section on profiles" },
            { k: "require_approval", l: "Require admin approval for new listings" },
            { k: "verification_enabled", l: "Enable verification badge system" },
          ].map(({ k, l }) => (
            <label key={k} className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!settings[k]} onChange={e => set(k, e.target.checked)} className="accent-yellow-500 w-4 h-4" />
              <span className="text-sm font-semibold text-gray-700">{l}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="font-bold text-gray-900 mb-2">Hero Section</div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Heading</label><input value={settings.hero?.heading || ""} onChange={e => setHero("heading", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" /></div>
          <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Label</label><input value={settings.hero?.label || ""} onChange={e => setHero("label", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" /></div>
        </div>
        <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Subheading</label><textarea value={settings.hero?.subheading || ""} onChange={e => setHero("subheading", e.target.value)} rows={2} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none resize-none" /></div>
        <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Background Image URL</label><input value={settings.hero?.image_url || ""} onChange={e => setHero("image_url", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" /></div>
        <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Background Color</label><input type="color" value={settings.hero?.bg_color || "#0F1E38"} onChange={e => setHero("bg_color", e.target.value)} className="h-10 rounded-lg border border-gray-200 px-2" /></div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="font-bold text-gray-900 mb-2">Email Templates</div>
        <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">New Inquiry Notification Subject</label><Inp k="inquiry_email_subject" placeholder="New inquiry from {name} for {business_name}" /></div>
        <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Listing Approved Subject</label><Inp k="approved_email_subject" placeholder="Your listing is now live on CPM Marketplace" /></div>
        <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Listing Rejected Subject</label><Inp k="rejected_email_subject" placeholder="Update on your CPM Marketplace listing" /></div>
      </div>
      <button onClick={save} disabled={saving} className="px-8 py-3 rounded-xl font-bold text-white disabled:opacity-50" style={{ background: "#0F1E38" }}>{saving ? "Saving…" : "Save Settings"}</button>
    </div>
  );
}

// ── Embeddable inner content (used inside central Admin console) ───────────────
export function MarketplaceAdminContent() {
  const [activeTab, setActiveTab] = useState("pending");
  const CONTENT = { pending: <PendingTab />, listings: <AllListingsTab />, categories: <CategoriesTab />, settings: <SettingsTab /> };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-1.5 pb-3 border-b border-gray-100">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === id ? "bg-[#0F1E38] text-white" : "text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <div className="mb-4 font-bold text-base text-gray-800 flex items-center gap-2">
          {(() => { const t = TABS.find(t => t.id === activeTab); return t ? <><t.icon size={16} className="text-gray-500" /> {t.label}</> : null; })()}
        </div>
        {CONTENT[activeTab]}
      </div>
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────────
export default function MarketplaceAdmin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("pending");

  if (!user || (user.role !== "super_admin" && user.role !== "parish_admin")) {
    return <div className="text-center py-20 text-gray-500">Access restricted to administrators.</div>;
  }

  const CONTENT = { pending: <PendingTab />, listings: <AllListingsTab />, categories: <CategoriesTab />, settings: <SettingsTab /> };

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }}>
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/marketplace" className="text-sm font-semibold text-yellow-600">← Marketplace</Link>
            <span className="text-gray-300">·</span>
            <span className="font-bold text-gray-900">Marketplace Admin</span>
          </div>
          <Link to="/app/admin" className="text-sm text-gray-500 hover:text-gray-700">Main Admin →</Link>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        <aside className="w-48 shrink-0">
          <nav className="space-y-1">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setActiveTab(id)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors text-left ${activeTab === id ? "text-white" : "text-gray-600 hover:bg-gray-100"}`} style={activeTab === id ? { background: "#0F1E38" } : {}}>
                <Icon size={15} />{label}
              </button>
            ))}
          </nav>
        </aside>
        <div className="flex-1 min-w-0">
          <div className="mb-4 font-bold text-lg text-gray-900">{TABS.find(t => t.id === activeTab)?.label}</div>
          {CONTENT[activeTab]}
        </div>
      </div>
    </div>
  );
}
